/**
 * @module components/PercentageOfColumnTotal
 * @description 表格「列总计占比」配置（ChartSettingsV2）
 *
 * 位置：高级 → 表格格式 下方
 * 默认态：收起
 * 可见性：仅在「表格类型 + 聚合模式」下展示（`chartVisibility: { [ChartType.Table]: { visible: isAggregation } }`）；
 *   明细模式（`qb.mode === 'raw'`）不渲染该组（与 PivotTable / TableFieldSetting / DimensionPercentage 保持一致）
 *
 * 状态管理模式：
 *   读/写: useSettingObjectState('column_percentage')
 *   column_percentage → column_sort 联动由 ChartSettingsContext reducer 处理
 */

import React, { useMemo, useCallback } from 'react';
import { useIntl } from 'umi';
import ConfigCollapseCard from '@/pages/datalab/components/ConfigCollapseCard';
import TagFilterSelect from '@/pages/datalab/components/TagFilterSelect';
import { getLanguage } from '@home/utils/common';
import { LANGUAGE_EN } from '@home/_config/config';
import type { IColumnInfo } from '@dbl/core/dataSource';
import { DATALAB_SPLIT_STR } from '@dbl/core/global';
import { useSettingObjectState } from '../../context';
import { useQuerySelector } from '../../hooks/deps';
import type { IMetricPercentageItem } from '../../types/settings/columnPercentage';
import { EMPTY_COLUMN_INFO_LIST } from '../../utils/constants';
import './index.scss';

interface IOptionItem {
  text: string;
  value: string;
}

interface PercentageOfColumnTotalProps {
  visibleItems?: Record<string, boolean>;
}

const PercentageOfColumnTotal: React.FC<PercentageOfColumnTotalProps> = () => {
  const { formatMessage, locale } = useIntl();
  const isEn = locale === LANGUAGE_EN;
  const columnMetrics = useQuerySelector(
    s => s.columnMetrics ?? EMPTY_COLUMN_INFO_LIST,
  );
  const { value, setValue } = useSettingObjectState('column_percentage');

  const options = useMemo((): IOptionItem[] => {
    const metrics = columnMetrics as IColumnInfo[];
    return metrics.map(item => ({
      text: getLanguage(isEn, item?.column_display_en, item?.column_display),
      value: `${item?.table_code || 't0'}${DATALAB_SPLIT_STR}${item?.column_name}`,
    }));
  }, [columnMetrics, isEn]);

  const handleChange = useCallback(
    (tags: string[]) => {
      const values: IMetricPercentageItem[] = tags.map(item => {
        const [code, name] = item.split(DATALAB_SPLIT_STR);
        return { metric: name, table_code: code };
      });
      setValue(values);
    },
    [setValue],
  );

  return (
    <ConfigCollapseCard
      title={formatMessage({ id: 'dbl_chart_column_percent_title' })}
      info={{
        tipText: formatMessage({ id: 'dbl_chart_column_percent_title_info' }),
      }}
      className="dbl-column-percentage"
      defaultActiveIds={[]}
    >
      <div className="dbl-column-percentage-label">
        {formatMessage({ id: 'dbl_chart_metric' })}
      </div>
      <TagFilterSelect
        className="dbl-column-percentage-select"
        value={
          value.map(
            item => `${item.table_code}${DATALAB_SPLIT_STR}${item.metric}`,
          )
        }
        name=" "
        locale={locale}
        placeholder={formatMessage({
          id: 'dbl_chart_column_percent_placeholder',
        })}
        tooltip={null}
        options={options}
        listWidth={244}
        listHeight={180}
        searchable={false}
        onChange={handleChange}
      />
    </ConfigCollapseCard>
  );
};

export default React.memo(PercentageOfColumnTotal);
